// SPDX-License-Identifier: AGPL-3.0-or-later
// Numbers the dashboard shows, derived from transactions and the plan.
import {
  budgetForPeriod,
  calendarYear,
  dailyTotals,
  paceStatus,
  periodRange,
  type CalendarMonth,
  type Cents,
  type IsoDate,
  type PaceStatus,
  type Period,
  isoDate,
  monthOf,
  yearOf,
} from "@almanac/domain";
import { depth, mainOf, NOT_SPENDING, within } from "./categories.js";
import { commitments, stillExpected } from "./commitments.js";
import type { BudgetPlan, CalendarEvent, Transaction } from "./model.js";

export function isSpending(t: Transaction): boolean {
  return t.amount < 0 && !NOT_SPENDING.has(mainOf(t.category ?? "uncategorized"));
}

/** Spending is shown as a positive number. */
export function spentBetween(transactions: readonly Transaction[], start: IsoDate, end: IsoDate): Cents {
  let total = 0;
  for (const t of transactions) if (t.date >= start && t.date <= end && isSpending(t)) total -= t.amount;
  return total;
}

/** The monthly amount meant to be spent: the pool minus what is set aside as savings. */
export function spendingBudget(plan: BudgetPlan): Cents {
  return plan.lines.filter((l) => !NOT_SPENDING.has(l.id)).reduce((s, l) => s + l.amount, 0);
}

export interface PeriodStatus extends PaceStatus {
  period: Period;
  budget: Cents;
}

export function statuses(transactions: readonly Transaction[], plan: BudgetPlan, today: IsoDate): PeriodStatus[] {
  const monthly = spendingBudget(plan);
  return (["day", "week", "month"] as const).map((period) => {
    const range = periodRange(period, today);
    const budget = budgetForPeriod(monthly, range);
    const spent = spentBetween(transactions, range.start, range.end < today ? range.end : today);
    return { period, budget, ...paceStatus(spent, budget, range, today) };
  });
}

export interface Kpis {
  leftThisMonth: Cents;
  spentToday: Cents;
  monthToDate: Cents;
  monthBudget: Cents;
  netThisMonth: Cents;
  dailyBudget: Cents;
  /** Event costs still expected this month. */
  scheduled: Cents;
}

export function kpis(transactions: readonly Transaction[], plan: BudgetPlan, today: IsoDate, events: readonly CalendarEvent[] = []): Kpis {
  const month = periodRange("month", today);
  const monthBudget = spendingBudget(plan);
  const monthToDate = spentBetween(transactions, month.start, today);
  const scheduled = scheduledBetween(events, transactions, month.start, month.end, today);
  let net = 0;
  for (const t of transactions) if (t.date >= month.start && t.date <= today && t.category !== "transfers-in") net += t.amount;
  return {
    leftThisMonth: monthBudget - monthToDate - scheduled,
    spentToday: spentBetween(transactions, today, today),
    monthToDate,
    monthBudget,
    netThisMonth: net,
    dailyBudget: budgetForPeriod(monthBudget, periodRange("day", today)),
    scheduled,
  };
}

/** The total of event costs in a range that are still expected as of today. */
export function scheduledBetween(
  events: readonly CalendarEvent[],
  transactions: readonly Transaction[],
  start: IsoDate,
  end: IsoDate,
  today: IsoDate,
): Cents {
  return stillExpected(commitments(events, transactions, start, end), today).reduce((s, c) => s + c.amount, 0);
}

export interface Slice {
  id: string;
  amount: Cents;
  /** True when some of this slice sits in sub-categories, so it can be opened. */
  deeper?: boolean;
}

/** The biggest main spending categories in a range; the rest are grouped as "other". */
export function topCategories(transactions: readonly Transaction[], start: IsoDate, end: IsoDate, count = 5): Slice[] {
  return spendingBreakdown(transactions, start, end, null, count);
}

/**
 * Spending in a range, one level below `parent` (main categories when null).
 * Spending filed on `parent` itself keeps the parent's id. Beyond `count`
 * slices the smallest are grouped as "rest".
 */
export function spendingBreakdown(
  transactions: readonly Transaction[],
  start: IsoDate,
  end: IsoDate,
  parent: string | null = null,
  count = 5,
): Slice[] {
  const totals = new Map<string, Slice>();
  const level = parent ? depth(parent) + 1 : 1;
  for (const t of transactions) {
    if (t.date < start || t.date > end || !isSpending(t)) continue;
    const category = t.category ?? "uncategorized";
    if (parent && !within(category, parent)) continue;
    const parts = category.split("/");
    const id = parts.slice(0, level).join("/");
    const slice = totals.get(id) ?? { id, amount: 0, deeper: false };
    slice.amount -= t.amount;
    if (parts.length > level) slice.deeper = true;
    totals.set(id, slice);
  }
  const sorted = [...totals.values()].sort((a, b) => b.amount - a.amount);
  const top = sorted.slice(0, count);
  const rest = sorted.slice(count).reduce((s, x) => s + x.amount, 0);
  return rest > 0 ? [...top, { id: "rest", amount: rest }] : top;
}

/** The first day of the month `n` months from `date`'s month. */
export function shiftMonth(date: IsoDate, n: number): IsoDate {
  const index = yearOf(date) * 12 + monthOf(date) - 1 + n;
  return isoDate(Math.floor(index / 12), (index % 12) + 1, 1);
}

export interface MonthOutlook {
  start: IsoDate;
  end: IsoDate;
  /** Where the month sits relative to today. */
  when: "past" | "current" | "future";
  budget: Cents;
  /** Spent so far (nothing yet for a future month). */
  spent: Cents;
  /** Event costs in the month still expected: not yet matched to a payment. */
  scheduled: Cents;
}

/** One month of the outlook: what was planned, spent, and is still scheduled. */
export function monthOutlook(
  transactions: readonly Transaction[],
  plan: BudgetPlan,
  events: readonly CalendarEvent[],
  month: IsoDate,
  today: IsoDate,
): MonthOutlook {
  const { start, end } = periodRange("month", month);
  const when = end < today ? "past" : start > today ? "future" : "current";
  const spent = when === "future" ? 0 : spentBetween(transactions, start, end < today ? end : today);
  const scheduled = when === "past" ? 0 : scheduledBetween(events, transactions, start, end, today);
  return { start, end, when, budget: spendingBudget(plan), spent, scheduled };
}

/** The plan's spending lines as donut slices, for a month that has not happened yet. */
export function plannedSlices(plan: BudgetPlan, count = 5): Slice[] {
  const sorted = plan.lines
    .filter((l) => !NOT_SPENDING.has(l.id) && l.amount > 0)
    .map((l) => ({ id: l.id, amount: l.amount }))
    .sort((a, b) => b.amount - a.amount);
  const rest = sorted.slice(count).reduce((s, x) => s + x.amount, 0);
  return rest > 0 ? [...sorted.slice(0, count), { id: "rest", amount: rest }] : sorted.slice(0, count);
}

/**
 * Spending per month of a year, for all spending (null) or one category and
 * everything under it. Months after today are null.
 */
export function monthlySpending(transactions: readonly Transaction[], year: number, today: IsoDate, id: string | null): (Cents | null)[] {
  const totals: (Cents | null)[] = Array.from({ length: 12 }, (_, i) => (isoDate(year, i + 1, 1) > today ? null : 0));
  for (const t of transactions) {
    if (yearOf(t.date) !== year || t.date > today || t.amount >= 0) continue;
    if (id === null ? !isSpending(t) : !within(t.category ?? "uncategorized", id)) continue;
    const m = monthOf(t.date) - 1;
    totals[m] = (totals[m] ?? 0) - t.amount;
  }
  return totals;
}

export type CalendarMetric = "net" | "budget";

/**
 * Daily values for the calendar. "net" is money in minus money out (not
 * counting moves between your own accounts). "budget" is the daily budget
 * minus that day's spending, so a bar above the plane is a day under budget.
 */
export function calendarData(
  transactions: readonly Transaction[],
  plan: BudgetPlan,
  year: number,
  metric: CalendarMetric,
  today: IsoDate,
): CalendarMonth[] {
  if (metric === "net") {
    const totals = dailyTotals(transactions.filter((t) => t.category !== "transfers-in" && t.date.startsWith(`${year}-`)));
    return calendarYear(year, (d) => totals.get(d) ?? 0);
  }
  const daily = budgetForPeriod(spendingBudget(plan), periodRange("day", today));
  const spent = dailyTotals(transactions.filter(isSpending).map((t) => ({ date: t.date, amount: -t.amount })));
  return calendarYear(year, (d) => (d > today ? 0 : daily - (spent.get(d) ?? 0)));
}

export function transactionsOn(transactions: readonly Transaction[], date: IsoDate): Transaction[] {
  return transactions.filter((t) => t.date === date).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
}

/** Actual spending this month, per exact category. Roll it up with `rolledUp`. */
export function actualsThisMonth(transactions: readonly Transaction[], today: IsoDate): Map<string, Cents> {
  const month = periodRange("month", today);
  const totals = new Map<string, Cents>();
  for (const t of transactions) {
    if (t.date < month.start || t.date > today || t.amount >= 0) continue;
    const id = t.category ?? "uncategorized";
    totals.set(id, (totals.get(id) ?? 0) - t.amount);
  }
  return totals;
}

/** A transaction still waiting for someone to pick its category. */
export function needsCategory(t: Transaction): boolean {
  return t.category === null || t.category === "uncategorized";
}

export function todayIso(): IsoDate {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Spending for a category and everything under it. */
export function rolledUp(actuals: ReadonlyMap<string, Cents>, id: string): Cents {
  let total = 0;
  for (const [key, amount] of actuals) if (within(key, id)) total += amount;
  return total;
}

/** Average monthly spending per exact category over the last `months` full months before `today`'s month. */
export function recentMonthlyActuals(transactions: readonly Transaction[], today: IsoDate, months = 3): Map<string, Cents> {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const startIndex = y * 12 + m - 1 - months;
  const start = `${Math.floor(startIndex / 12)}-${String((startIndex % 12) + 1).padStart(2, "0")}-01`;
  const end = `${today.slice(0, 7)}-01`;
  const totals = new Map<string, Cents>();
  for (const t of transactions) {
    if (t.date < start || t.date >= end || t.amount >= 0) continue;
    const id = t.category ?? "uncategorized";
    totals.set(id, (totals.get(id) ?? 0) - t.amount);
  }
  for (const [id, amount] of totals) totals.set(id, Math.round(amount / months));
  return totals;
}
